// components/DataGrid/DataGrid.jsx
import { LicenseInfo } from '@mui/x-license';
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  DataGridPro,
  getGridNumericOperators,
  getGridBooleanOperators,
  useGridApiRef,
  GRID_DETAIL_PANEL_TOGGLE_FIELD,
} from "@mui/x-data-grid-pro";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { ViewType, FilterConfig } from "../../Enums.js";
import { getNestedPropertyValue } from "../../fields/Fields.js";
import DetailPanel from "./DetailPanel/DetailPanel.js";
import { usePersistedNotifications } from "./usePersistedNotifications.js";
import { NOTIFICATION_FLASH_DURATION_MS } from "../../helpers/MessageHelpers.js";
import "@mui/material/styles";
import "dayjs/locale/en-gb";
import "./Table.css";
import "./Datagrid.css";


LicenseInfo.setLicenseKey('e65f3368d3ee1a8b47c99a1caaf3a68dTz0xMjU4MjIsRT0xODAyOTk1MTk5MDAwLFM9cHJvLExNPXN1YnNjcmlwdGlvbixQVj1RMy0yMDI0LEtWPTI=');

/* ----------------------------- helpers ----------------------------- */

function getSortModel(columns = []) {
  const sortable = columns.filter((c) => c.sort);
  sortable.sort((a, b) => (a.sort?.order ?? 0) - (b.sort?.order ?? 0));
  return sortable.map((c) => ({ field: c.field, sort: c.sort.direction }));
}

function getPinnedColumns(columns = []) {
  // Reserved MUI columns, no view config entry, hardcoded pinned
  const left = ["__check__", GRID_DETAIL_PANEL_TOGGLE_FIELD];
  for (const col of columns) {
    if (col.pinned === "left" && !left.includes(col.field)) left.push(col.field);
  }
  return { left, right: [] };
}

function hasRowNotification(row) {
  return Array.isArray(row?.notifications) && row.notifications.length > 0;
}

function getNotificationRowClassName(params) {
  return hasRowNotification(params.row) ? "row-notification-flash" : "";
}

function getNotificationCellClassName(params) {
  if (params.field !== "notifications") return "";
  return hasRowNotification(params.row) ? "cell-notification-flash" : "";
}

function coerceValueToColumnType(column, raw) {
  if (raw == null) return raw;
  switch (column.type) {
    case "number":
      return typeof raw === "number" ? raw : Number(raw);
    case "boolean":
      return typeof raw === "boolean" ? raw : String(raw).toLowerCase() === "true";
    case "date":
    case "dateTime":
      return raw; // adjust if your operators expect Date objects
    default:
      return raw;
  }
}

function buildFilterModelFromColumns(columns = []) {
  const items = [];
  for (const col of columns) {
    const cfg = col.filter; // custom config { operator, value }
    if (!cfg) continue;

    const allowed =
      Array.isArray(col.filterOperators) && col.filterOperators.length > 0
        ? col.filterOperators.some((o) => o.value === cfg.operator)
        : true;
    if (!allowed) continue;

    items.push({
      id: `${col.field}-${cfg.operator}`,
      field: col.field,
      operator: cfg.operator,
      value: coerceValueToColumnType(col, cfg.value),
    });
  }
  return { items, quickFilterValues: [] };
}

// choose an operator that exists on a given field; fallback to first available
function pickOperator(columns, field, preferred) {
  const col = columns.find((c) => c.field === field);
  if (!col) return preferred;
  const ops = Array.isArray(col.filterOperators) ? col.filterOperators : [];
  if (ops.length === 0) return preferred;
  return ops.some((o) => o.value === preferred) ? preferred : ops[0].value;
}

// normalize column types/operators for filtering
function normalizeColumnsForFiltering(cols) {
  return cols.map((col) => {
    // numeric progress fields
    if (col.field === "progress" || col.field === "2progress") {
      return {
        ...col,
        type: "number",
        filterOperators: [...getGridNumericOperators()],
      };
    }
    // boolean flags
    if (col.field === "complete" || col.field === "reviewer" || col.field === "submitter" || col.field === "admin") {
      return {
        ...col,
        type: "boolean",
        filterOperators: [...getGridBooleanOperators()],
        // Normalize possible "true"/"false" strings to booleans for filtering/sorting consistency
        valueGetter:
          col.valueGetter ??
          ((params) => {
            const v = params.value ?? params.row?.[col.field];
            return v === true || v === "true";
          }),
      };
    }
    return col;
  });
}

/* ----------------------------- component ----------------------------- */

const DataGrid = ({
  fields,
  rows,
  logs,
  selectedRows,
  onColumnWidthChange,
  onColumnOrderChange,
  onRowUpdate,
  onCellUpdate,
  onFileUpdate,
  onReviewerChange,
  onResetPassword,
  onSendPasswordResetEmail,
  onAdminise,
  onSelectionChange,
  customTheme,
  selectedView,
  projectCode,
  filterConfigType,
  searchTerm,
}) => {
  // visible columns first, then normalize for types/operators
  const columns = useMemo(() => {
    const visible = (fields ?? []).filter((c) => c.visible === true);
    //console.log("Visible columns for filtering:", visible);  
    return normalizeColumnsForFiltering(visible);
  }, [fields]);

  // Programmatic filter model (presets + dynamic-from-columns + quick search)
  const filterModel = useMemo(() => {
    const presetItems = [];
    switch (filterConfigType) {
      case FilterConfig.INCOMPLETE: {
        const op = pickOperator(columns, "progress", "!=");
        presetItems.push({
          id: `progress-${op}-incomplete`,
          field: "progress",
          operator: op,
          value: 100,
        });
        break;
      }
      case FilterConfig.SUBMITTED: {
        const op = pickOperator(columns, "complete", "is"); // boolean operator
        presetItems.push({
          id: `complete-${op}-submitted`,
          field: "complete",
          operator: op,
          value: true,
        });
        break;
      }
      case FilterConfig.COMPLETE: {
        const op = pickOperator(columns, "progress", "=");
        presetItems.push({
          id: `progress-${op}-complete`,
          field: "progress",
          operator: op,
          value: 100,
        });
        break;
      }
      case FilterConfig.REVIEWERS: {
        const op = pickOperator(columns, "reviewer", "is"); // boolean operator
        presetItems.push({
          id: `reviewer-${op}-flag`,
          field: "reviewer",
          operator: op,
          value: true,
        });
        break;
      }
      case FilterConfig.ALL:
      default:
        break;
    }

    const dynamicItems = buildFilterModelFromColumns(columns).items;

    const coercedPreset = presetItems.map((it) => {
      const col = columns.find((c) => c.field === it.field);
      return col ? { ...it, value: coerceValueToColumnType(col, it.value) } : it;
    });

    const quick =
      typeof searchTerm === "string" && searchTerm.trim() !== ""
        ? [searchTerm]
        : [];

    return {
      items: [...coercedPreset, ...dynamicItems],
      quickFilterValues: quick,
    };
  }, [columns, filterConfigType, searchTerm]);

  const [expandedRowId, setExpandedRowId] = useState(null);
  const [sortModel, setSortModel] = useState(() => getSortModel(columns));
  // Sync with columns, avoids one-render lag causing duplicate row-key warnings
  const pinnedColumns = useMemo(() => getPinnedColumns(columns), [columns]);

  // Persisted notifications per row, resets on view switch, cleared on dismiss
  const { persistedByRow, dismiss: dismissRowNotifications } = usePersistedNotifications(rows);

  // field key -> column header label, for notification feed display
  const fieldLabelsByKey = useMemo(() => {
    const map = {};
    (fields || []).forEach((f) => {
      if (f?.field) map[f.field] = f.headerName || f.field;
    });
    return map;
  }, [fields]);

  // Whichever field the active view itself labels "Title" — reused rather than a
  // guessed hardcoded path, since the grid already renders this column correctly.
  const titleField = useMemo(
    () => (fields || []).find((f) => f?.field === "title" || f?.headerName === "Title"),
    [fields]
  );

  const displayRows = useMemo(
    () => rows.map((row) => ({
      ...row,
      persistedNotifications: persistedByRow[row.id] || [],
      entityTitle: titleField ? getNestedPropertyValue(row, titleField.dataSource) : undefined,
      fieldLabelsByKey,
      onDismissNotifications: () => dismissRowNotifications(row.id),
    })),
    [rows, persistedByRow, titleField, fieldLabelsByKey, dismissRowNotifications]
  );

  const [visibleRowWidth, setVisibleRowWidth] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const apiRef = useGridApiRef();
  const gridVisibleRef = useRef();
  const getDetailPanelHeight = useCallback(() => 640, []);

  // Real rendered width of the pinned-column boundary, measured from the DOM
  // (apiRef.getColumnPosition) rather than summed from column config, which
  // required guessing every pinned column's width and got it wrong for views
  // other than Users. Drives the second divider line (Datagrid.css) via the
  // --pinned-boundary-width CSS var set on this component's wrapper below.
  const [pinnedLeftWidth, setPinnedLeftWidth] = useState(150);

  const firstUnpinnedField = useMemo(() => {
    const pinnedSet = new Set(pinnedColumns.left);
    return columns.find((c) => !pinnedSet.has(c.field))?.field;
  }, [columns, pinnedColumns]);

  useEffect(() => {
    if (!firstUnpinnedField) return;

    const update = () => {
      const position = apiRef.current?.getColumnPosition?.(firstUnpinnedField);
      if (typeof position === "number") setPinnedLeftWidth(position);
    };

    update();
    return apiRef.current?.subscribeEvent?.("columnWidthChange", update);
  }, [firstUnpinnedField, apiRef]);

  // Reset the grid sort when the view/columns change so sort state does not leak between views.
  useEffect(() => {
    const nextSortModel = getSortModel(columns);
    console.log("DG: sort model initialized for view", {
      viewLabel: selectedView?.viewLabel ?? null,
      viewType: selectedView?.type ?? null,
      sortModel: nextSortModel,
      sortableColumns: columns
        .filter((column) => column?.sort)
        .map((column) => ({
          field: column.field,
          sort: column.sort,
        })),
    });
    setSortModel(nextSortModel);
  }, [columns, selectedView?.viewLabel, selectedView?.type]);

  // Track scrollLeft to align the fixed detail container
  useEffect(() => {
    const scroller = document.querySelector(".MuiDataGrid-virtualScroller");
    if (!scroller) return;
    const onScroll = () => setScrollLeft(scroller.scrollLeft);
    scroller.addEventListener("scroll", onScroll);
    return () => scroller.removeEventListener("scroll", onScroll);
  }, []);

  // Track visible viewport width for detail panel alignment
  useEffect(() => {
    const handleResize = () => {
      const width = apiRef.current.getRootDimensions()?.viewportInnerSize.width;
      setVisibleRowWidth(width);
    };
    const unsubscribe = apiRef.current.subscribeEvent(
      "viewportInnerSizeChange",
      handleResize
    );
    handleResize();
    return () => unsubscribe();
  }, []);

  const handleProcessRowUpdate = async (updatedRow, originalRow) => {
    const result = await onRowUpdate(updatedRow, originalRow);
    return result;
  };

  const handleCellClick = (params) => {
    if (!params.field.includes("__check__")) {
      params.defaultMuiPrevented = true;
    }
  };

  const handleProcessRowUpdateError = useCallback((error) => {
    console.error("An error occurred while updating the row:", error);
  }, []);

  const handleProcessRowUpdate2 = (updatedRow, originalRow) => {
    return handleProcessRowUpdate(updatedRow, originalRow);
  };

  const getDetailPanelContent = useCallback(
    ({ row }) => {
      if (expandedRowId !== row.id) return <div style={{ display: "none" }} />;
      return (
        <DetailPanel
          fields={fields}
          row={row}
          logs={logs}
          containerWidth={visibleRowWidth}
          offsetLeft={scrollLeft}
          viewType={selectedView.type}
          projectCode={projectCode}
          theme={customTheme}
          onCellUpdate={onCellUpdate}
          onFileUpdate={onFileUpdate}
          onReviewerChange={onReviewerChange}
          onResetPassword={onResetPassword}
          onSendPasswordResetEmail={onSendPasswordResetEmail}
          onAdminise={onAdminise}
        />
      );
    },
    [
      fields,
      logs,
      expandedRowId,
      visibleRowWidth,
      scrollLeft,
      selectedView.type,
      projectCode,
      customTheme,
      onCellUpdate,
      onFileUpdate,
      onReviewerChange,
      onResetPassword,
      onSendPasswordResetEmail,
      onAdminise,
    ]
  );

  const handleDetailPanelExpandedRowIdsChange = (ids) => {
    const newExpandedRowId = ids[0] || null;
    setExpandedRowId(newExpandedRowId);

    const visibleGridWidth =
      apiRef.current.getRootDimensions()?.viewportInnerSize.width;
    setVisibleRowWidth(visibleGridWidth);
  };

  const handleColumnWidthChange = (params) => {
    onColumnWidthChange?.(params);
  };

  const handleColumnOrderChange = (newColumns) => {
    onColumnOrderChange(newColumns);
  };

const pxToNumber = (px, fallback) => {
  const n = parseInt(String(px || ""), 10);
  return Number.isFinite(n) ? n : fallback;
};

const gridFontPx =
  customTheme.fontSize;

const baseFontSize = pxToNumber(gridFontPx, 14);

const fontFamily =
  customTheme.fontFamily;

const fontSizePx = `${baseFontSize}px`;

const theme = createTheme({
  typography: {
    fontFamily,
    fontSize: baseFontSize, // MUI expects number here
    allVariants: {
      fontFamily,
      fontSize: fontSizePx,
      lineHeight: 1.2,
    },
    body1: { fontSize: fontSizePx },
    body2: { fontSize: fontSizePx },
    button: { fontSize: fontSizePx, textTransform: "none" },
  },

  components: {
    MuiTypography: {
      styleOverrides: {
        root: { fontFamily, fontSize: fontSizePx },
      },
    },

    MuiInputBase: {
      styleOverrides: {
        root: { fontFamily, fontSize: fontSizePx },
        input: { fontFamily, fontSize: fontSizePx },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: { fontFamily, fontSize: fontSizePx },
        input: { fontFamily, fontSize: fontSizePx },
      },
    },

    MuiSelect: {
      styleOverrides: {
        select: { fontFamily, fontSize: fontSizePx },
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: { fontFamily, fontSize: fontSizePx },
      },
    },

    MuiButton: {
      styleOverrides: {
        root: { fontFamily, fontSize: fontSizePx, textTransform: "none" },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: { fontFamily, fontSize: fontSizePx },
        label: { fontFamily, fontSize: fontSizePx },
      },
    },

    MuiDataGrid: {
      styleOverrides: {
        root: {
          "& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader": {
            backgroundColor: customTheme.columnHeaderBackgroundColour,
            color: customTheme.columnHeaderTextColour,
            fontFamily,
            fontSize: fontSizePx,
            lineHeight: 1.2,
          },

          "& .MuiDataGrid-cell": {
            fontFamily,
            fontSize: fontSizePx,
            lineHeight: 1.2,

            // keep these for safety: they force editors/inputs inside cells to match
            "& input": {
              fontFamily,
              fontSize: fontSizePx,
              lineHeight: 1.2,
            },
            "& .MuiInputBase-root": {
              fontFamily,
              fontSize: fontSizePx,
              lineHeight: 1.2,
            },

            "&:focus-within": {
              outline: "none",
            },

            "&.Mui-selected, &.MuiDataGrid-cell--editing": {
              outline: "none",
              backgroundColor: "transparent",
              "&:hover": { backgroundColor: "transparent" },
            },
          },

          "& .MuiDataGrid-cell--editing": {
            padding: "none",
            display: "flex",
            boxShadow:
              "0px 3px 1px -2px rgba(0,0,0,0), 0px 2px 2px 0px rgba(0,0,0,0), 0px 1px 5px 0px rgba(0,0,0,0)",
            outline: "none",
          },

          "& .MuiDataGrid-cell:focus-within": { outline: "none" },

          "& .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell": {
            backgroundColor: "transparent",
          },

          "& .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell.MuiDataGrid-cell--editing":
            { backgroundColor: "transparent" },
        },
      },
    },
  },
});

/*
console.log("DG columns inputs", {
  viewLabel: selectedView?.viewLabel,
  viewType: selectedView?.type,
  fieldsLen: Array.isArray(fields) ? fields.length : null,
});
console.log("Fields", fields);
*/

  return (
    <div
      style={{
        "--notification-row-colour": customTheme?.notificationRowColour,
        "--notification-cell-colour": customTheme?.notificationCellColour,
        "--notification-flash-duration": `${NOTIFICATION_FLASH_DURATION_MS}ms`,
        "--pinned-boundary-width": `${pinnedLeftWidth}px`,
      }}
    >
        <div className="myTableView">
          <div className="layout">
            <ThemeProvider theme={theme}>
              <DataGridPro
                key={`${selectedView?.viewLabel || "default"}-${selectedView?.id || "default"}`}
                className="myDataGrid"
                ref={gridVisibleRef}
                apiRef={apiRef}
                rows={displayRows}
                columns={columns}
                //onCellClick={handleCellClick}
                processRowUpdate={handleProcessRowUpdate2}
                onProcessRowUpdateError={handleProcessRowUpdateError}
                getRowClassName={getNotificationRowClassName}
                getCellClassName={getNotificationCellClassName}
                disableRowSelectionOnClick
                checkboxSelection
                {...([ViewType.SUBMISSIONS, ViewType.USERS].includes(selectedView.type) && {
                  getDetailPanelHeight,
                  getDetailPanelContent,
                  detailPanelExpandedRowIds: expandedRowId !== null ? [expandedRowId] : [],
                  onDetailPanelExpandedRowIdsChange: handleDetailPanelExpandedRowIdsChange,
                })}
                sortModel={sortModel}
                onSortModelChange={(newSortModel) => {
                  const defaultSortModel = getSortModel(columns);
                  const shouldIgnoreTransientEmptySort =
                    Array.isArray(newSortModel) &&
                    newSortModel.length === 0 &&
                    Array.isArray(defaultSortModel) &&
                    defaultSortModel.length > 0;

                  console.log("DG: sort model changed", {
                    viewLabel: selectedView?.viewLabel ?? null,
                    viewType: selectedView?.type ?? null,
                    sortModel: newSortModel,
                    defaultSortModel,
                    ignored: shouldIgnoreTransientEmptySort,
                  });

                  if (shouldIgnoreTransientEmptySort) {
                    return;
                  }

                  setSortModel(newSortModel);
                }}
                pinnedColumns={pinnedColumns}
                onRowSelectionModelChange={onSelectionChange}
                rowSelectionModel={selectedRows}
                onColumnOrderChange={handleColumnOrderChange}
                onColumnWidthChange={handleColumnWidthChange}
                filterModel={filterModel}
                disableColumnFilter
                disableColumnSelector
                disableDensitySelector
              />
            </ThemeProvider>
          </div>
        </div>
    </div>
  );
};

export default DataGrid;
